import { ReceiveCostLabel } from 'src/components/procurement/ReceiveCostLabel';
import { T, useGT } from 'gt-react';
import type {
  AssignmentLine,
  SuggestionOption,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { cn } from '@/lib/utils';
import { ppvDeltaPerUnit } from '../utils/lineBuyTerms';
import { NO_FACT } from '../utils/lineFacts';
import { Fact, FactGroup } from './LineFactsPanel';
import { formatUnitCost } from './unitCostFormat';
import { VendorPriceBreakTierList } from './VendorPriceBreakTierList';

function cost(value: number | null | undefined): string {
  return value == null ? NO_FACT : formatUnitCost(value);
}

function PpvDelta({ line }: { line: AssignmentLine }) {
  const delta = ppvDeltaPerUnit(line);
  if (delta === null || delta === 0) return <>{NO_FACT}</>;
  // The board's PPV Δ sign: + green when the buy is below the buyer cost.
  return (
    <span className={delta > 0 ? 'text-emerald-700' : 'text-red-700'}>
      {delta > 0 ? '+' : ''}
      {formatUnitCost(delta)}
    </span>
  );
}

/**
 * Where an option ships from: its vendor, the transfer's source, or for a
 * stock option the warehouse on its stock leg (the option's flat fields come
 * from buy and transfer legs only).
 */
function OptionSource({ option }: { option: SuggestionOption }) {
  const gt = useGT();
  if (option.action === 'stock') {
    const warehouse = option.legs.find(
      (leg) => leg.legType === 'stock',
    )?.sourceWarehouseName;
    return warehouse == null ? (
      <T context="line vendor tab option: filled from on-hand stock">Stock</T>
    ) : (
      <>
        {gt('Stock · {warehouse}', {
          context:
            'line vendor tab option: filled from on-hand stock at this warehouse',
          warehouse,
        })}
      </>
    );
  }
  return <>{option.supplierName ?? option.sourceWarehouseName ?? NO_FACT}</>;
}

function OptionList({
  options,
  chosenOptionId,
}: {
  options: readonly SuggestionOption[];
  chosenOptionId: string | undefined;
}) {
  if (options.length === 0) return null;
  const ranked = [...options].sort((a, b) => a.rank - b.rank);
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <T context="line vendor tab group: the engine's ranked sourcing options">
          Options
        </T>
      </div>
      <ol className="space-y-0.5">
        {ranked.map((option) => (
          <li
            key={option.optionId}
            className={cn(
              'flex items-baseline gap-2',
              option.optionId === chosenOptionId && 'font-medium',
            )}
            title={option.rationale ?? undefined}
          >
            <span className="w-4 shrink-0 text-right tabular-nums text-muted-foreground">
              {option.rank}
            </span>
            <span className="min-w-0 truncate">
              <OptionSource option={option} />
            </span>
            <span className="ml-auto shrink-0 tabular-nums">
              {cost(option.unitCost)}
            </span>
            {option.rationale && (
              <span className="hidden min-w-0 max-w-[40ch] truncate text-muted-foreground xl:inline">
                {option.rationale}
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * The line's cost and buying terms in one place (GEN-3285): buyer cost
 * against what it buys at, PPV, the PO terms the buyer set, the contract
 * prices and price breaks that apply, and the engine's ranked options with
 * why. Display only; the board's columns stay where costs are edited.
 */
export function LineVendorCostTab({
  line,
  options,
  chosenOptionId,
}: {
  /** Null while the cart's lines load. */
  line: AssignmentLine | null;
  options: readonly SuggestionOption[] | undefined;
  chosenOptionId: string | null | undefined;
}) {
  if (line === null) return null;
  const top = line.topOption;
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 p-3 text-xs lg:grid-cols-4">
      <FactGroup
        title={<T context="line vendor tab group: unit costs">Cost</T>}
      >
        <Fact
          label={
            <T context="line vendor tab: system (list) unit cost">Buyer cost</T>
          }
        >
          {cost(line.buyerUnitCost)}
        </Fact>
        <Fact
          label={
            <T context="line vendor tab: unit cost the line buys at">
              Buy cost
            </T>
          }
        >
          {cost(top?.unitCost)}
        </Fact>
        <Fact
          label={
            <T context="line vendor tab: buyer's one-time special cost">
              Revised cost
            </T>
          }
        >
          {cost(line.buyTerms.revisedUnitCost)}
        </Fact>
        <Fact
          label={
            <T context="line vendor tab: purchase price variance per unit">
              PPV Δ
            </T>
          }
        >
          <PpvDelta line={line} />
        </Fact>
        <Fact
          label={
            <T context="line vendor tab: the line's extended buy cost">
              Extended
            </T>
          }
        >
          {cost(top?.totalCost)}
        </Fact>
      </FactGroup>
      <FactGroup
        title={<T context="line vendor tab group: PO line terms">PO terms</T>}
      >
        <Fact
          label={
            <T context="line vendor tab: PO contract number">Contract #</T>
          }
        >
          {line.buyTerms.contractNumber ?? NO_FACT}
        </Fact>
        <Fact
          label={
            <T context="line vendor tab: which cost the PPV is received at">
              PPV at
            </T>
          }
        >
          <ReceiveCostLabel
            basis={line.buyTerms.ppvReceiveAt}
            cost={line.buyTerms.costConstraintUnitCost}
          />
        </Fact>
        <Fact label={<T context="line vendor tab: PO line comment">Comment</T>}>
          <span
            className="block max-w-[24ch] truncate"
            title={line.buyTerms.poLineComment ?? undefined}
          >
            {line.buyTerms.poLineComment ?? NO_FACT}
          </span>
        </Fact>
      </FactGroup>
      <div className="min-w-0 space-y-3">
        <FactGroup
          title={
            <T context="line vendor tab group: active contract prices">
              Contract prices
            </T>
          }
        >
          {line.buyContractPriceOptions.length === 0 ? (
            <span className="text-muted-foreground">{NO_FACT}</span>
          ) : (
            line.buyContractPriceOptions.map((option) => (
              <Fact
                key={option.costAgreementId}
                label={`#${option.contractNumber}`}
              >
                {formatUnitCost(option.unitCost)}
              </Fact>
            ))
          )}
        </FactGroup>
        <VendorPriceBreakTierList tiers={line.vendorPriceBreakTiers} />
      </div>
      <OptionList
        options={options ?? []}
        chosenOptionId={chosenOptionId ?? top?.optionId}
      />
    </div>
  );
}
